import { Grid } from '@mui/material';
import { getPersonFullNameWithTitle, PersonData } from '@ysura/common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailPageLayout } from '@/components/DetailPageLayout';
import { PageHeader, PageHeaderSkeleton } from '@/components/PageHeader';
import {
  PersonDataSkeletonStaticBlock,
  PersonDataStaticBlock,
} from '@/components/PersonData';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useGetActivityTypeListQuery } from '@/services/graphql/hooks';
import { parseActivityTypeConnection } from '@/services/graphql/parsers';
import { hasAssignActivityTypePermission } from '@/services/permission';

import { CreateActivityDialog } from '../Activity/TouchpointManagement';
import { ConsentSkeletonStaticBlock, ConsentStaticBlock } from './Consents';
import {
  RelatedOrganizationsSkeletonStaticBlock,
  RelatedOrganizationsStaticBlock,
} from './RelatedOrganizations';
import { SamplesSkeletonStaticBlock, SamplesStaticBlock } from './Samples';
import {
  TouchpointsHistoryStaticBlock,
  TouchpointsStaticBlock,
} from './Touchpoint';
import { TouchpointsHistorySkeletonStaticBlock } from './Touchpoint/TouchpoinsHistory';
import { TouchpointsSkeletonStaticBlock } from './Touchpoint/Touchpoints/TouchpointsSkeletonStaticBlock';

type ActivePersonProps = {
  person: PersonData | null;
  isPageLoading: boolean;
};

export const ActivePerson = ({ person, isPageLoading }: ActivePersonProps) => {
  const [isCreateTouchpointDialogOpen, setIsCreateTouchpointDialogOpen] =
    useState(false);
  const { t } = useTranslation();

  const { data: activityTypeData, loading: activityTypeLoading } =
    useGetActivityTypeListQuery();
  const activityTypeList = parseActivityTypeConnection(
    activityTypeData?.activityTypes
  );
  const { currentUser } = useCurrentUser();
  const canCreateActivity =
    currentUser?.permissions?.activity?.canCreateActivity &&
    hasAssignActivityTypePermission(activityTypeList);

  const canReadActivity = currentUser?.permissions?.activity?.canReadActivity;

  const shouldShowSkeleton = isPageLoading || activityTypeLoading || !person;

  const handleCloseCreateTouchpointDialog = () => {
    setIsCreateTouchpointDialogOpen(false);
  };

  const pageHeader =
    canCreateActivity && canReadActivity ? (
      <PageHeader
        title={getPersonFullNameWithTitle(person)}
        pageHeaderType={t('pages.personView.persons')}
        buttonText={t('components.touchpointManagement.createTouchpoint')}
        buttonHandler={() => setIsCreateTouchpointDialogOpen(true)}
        buttonTestId={'create-activity-btn'}
      />
    ) : (
      <PageHeader
        title={getPersonFullNameWithTitle(person)}
        pageHeaderType={t('pages.personView.persons')}
      />
    );

  return (
    <>
      <DetailPageLayout
        headerComp={
          shouldShowSkeleton ? <PageHeaderSkeleton showSubtitle /> : pageHeader
        }
        firstColumnComp={
          <>
            <Grid>
              {shouldShowSkeleton ? (
                <PersonDataSkeletonStaticBlock
                  title={'components.personDataCard.personData'}
                />
              ) : (
                <PersonDataStaticBlock person={person} />
              )}
            </Grid>
            <Grid>
              {shouldShowSkeleton ? (
                <RelatedOrganizationsSkeletonStaticBlock />
              ) : (
                <RelatedOrganizationsStaticBlock person={person} />
              )}
            </Grid>
          </>
        }
        middleColumnComp={
          <>
            {canReadActivity && (
              <>
                <Grid>
                  {shouldShowSkeleton ? (
                    <TouchpointsSkeletonStaticBlock />
                  ) : (
                    <TouchpointsStaticBlock />
                  )}
                </Grid>
                <Grid>
                  {shouldShowSkeleton ? (
                    <TouchpointsHistorySkeletonStaticBlock />
                  ) : (
                    <TouchpointsHistoryStaticBlock />
                  )}
                </Grid>
              </>
            )}
          </>
        }
        lastColumnComp={
          <>
            <Grid>
              {shouldShowSkeleton ? (
                <ConsentSkeletonStaticBlock />
              ) : (
                <ConsentStaticBlock person={person} />
              )}
            </Grid>
            {currentUser?.permissions?.person?.canReadSampleDrop && (
              <Grid>
                {shouldShowSkeleton ? (
                  <SamplesSkeletonStaticBlock />
                ) : (
                  <SamplesStaticBlock person={person} />
                )}
              </Grid>
            )}
          </>
        }
      />

      <CreateActivityDialog
        isOpen={isCreateTouchpointDialogOpen}
        selectedAttendee={person ?? undefined}
        onClose={handleCloseCreateTouchpointDialog}
      />
    </>
  );
};
